<template>
  <div class="osl-page animated revealUp">
    <!-- ───────────── Header ───────────── -->
    <div class="osl-header">
      <div>
        <div class="osl-header__title">
          Odoo Sync Logs
          <span class="osl-tag osl-tag--blue ml-2" :title="isLocalDashboard ? 'This dashboard is connected to a cinema server' : 'This dashboard is connected to the cloud server'">
            {{ isLocalDashboard ? "Cinema server" : "Cloud" }}
          </span>
        </div>
        <div class="osl-header__sub">
          Every request sent to Odoo, with its payload, Odoo's response and the sync status. Re-run or replay anything that didn't sync.
        </div>
      </div>
      <div class="osl-header__right">
        <span class="osl-muted osl-small" :title="lastUpdatedExact">
          <span v-if="hasActiveResends" class="osl-live-dot" title="Resends in progress. Refreshing every 5s."></span>
          {{ lastUpdatedLabel }}
        </span>
        <label class="osl-switch" title="Refresh every 30 seconds">
          <input v-model="autoRefresh" type="checkbox" />
          <span class="osl-switch__track"><span class="osl-switch__thumb"></span></span>
          <span class="osl-small">Auto-refresh</span>
        </label>
        <button class="osl-btn osl-btn--ghost" :disabled="loading" @click="refreshAll()">
          <i :class="['fa fa-refresh', { 'fa-spin': loading }]"></i> Refresh
        </button>
      </div>
    </div>

    <!-- ───────────── KPI strip ───────────── -->
    <div class="osl-kpis">
      <button
        v-for="kpi in kpis"
        :key="kpi.key"
        :class="['osl-kpi', 'osl-kpi--' + kpi.tone, { 'is-active': activeKpi === kpi.key }]"
        :title="kpi.hint"
        @click="applyKpi(kpi)"
      >
        <div class="osl-kpi__label">
          <i :class="['fa', kpi.icon]"></i> {{ kpi.label }}
        </div>
        <div class="osl-kpi__value">
          <span v-if="summaryLoading && !summaryLoaded" class="osl-skeleton osl-skeleton--num"></span>
          <template v-else>{{ formatNumber(kpi.value) }}</template>
        </div>
      </button>
    </div>

    <!-- ───────────── Filters ───────────── -->
    <div class="osl-card osl-filters">
      <div class="osl-filters__grid">
        <div class="osl-field osl-field--search">
          <label class="osl-field__label">Search</label>
          <div class="osl-input-icon">
            <i class="fa fa-search"></i>
            <input
              v-model="searchInput"
              class="osl-input"
              type="text"
              placeholder="Request ID, job ID, reference (sb_id:123) or error text"
              @keyup.enter="commitSearch"
            />
            <button v-if="searchInput" class="osl-input-clear" title="Clear" @click="clearSearch">
              <i class="fa fa-times"></i>
            </button>
          </div>
        </div>

        <div class="osl-field">
          <label class="osl-field__label">Status</label>
          <multiselect
            v-model="filters.status"
            class="osl-ms"
            :options="statusOptions"
            :multiple="true"
            :close-on-select="false"
            :searchable="false"
            :show-labels="false"
            label="label"
            track-by="value"
            placeholder="All statuses"
            @input="onFilterChange"
          >
            <template slot="selection" slot-scope="{ values }">
              <span v-if="values.length" class="osl-ms__summary">{{ summarizeSelection(values) }}</span>
            </template>
          </multiselect>
        </div>

        <div class="osl-field osl-field--wide">
          <label class="osl-field__label">Job type</label>
          <multiselect
            v-model="filters.job_type"
            class="osl-ms"
            :options="jobTypeOptions"
            :multiple="true"
            :close-on-select="false"
            :show-labels="false"
            label="label"
            track-by="value"
            group-values="items"
            group-label="group"
            :group-select="true"
            placeholder="All job types"
            @input="onFilterChange"
          >
            <template slot="selection" slot-scope="{ values }">
              <span v-if="values.length" class="osl-ms__summary">{{ summarizeSelection(values) }}</span>
            </template>
            <template slot="option" slot-scope="{ option }">
              <span v-if="option.$isLabel" class="osl-ms__group">{{ option.$groupLabel }}</span>
              <span v-else class="osl-ms__opt">
                {{ option.label }}
                <span class="osl-ms__code">{{ option.value }}</span>
              </span>
            </template>
          </multiselect>
        </div>

        <div class="osl-field">
          <label class="osl-field__label">Cinema</label>
          <multiselect
            v-model="filters.cinema_id"
            class="osl-ms"
            :options="cinemaOptions"
            :multiple="true"
            :close-on-select="false"
            :show-labels="false"
            label="label"
            track-by="value"
            placeholder="All cinemas"
            @input="onFilterChange"
          >
            <template slot="selection" slot-scope="{ values }">
              <span v-if="values.length" class="osl-ms__summary">{{ summarizeSelection(values) }}</span>
            </template>
          </multiselect>
        </div>

        <div class="osl-field">
          <label class="osl-field__label" title="The server that made the call. Resends run on this same server.">Executed on</label>
          <multiselect
            v-model="filters.source_cinema_id"
            class="osl-ms"
            :options="sourceOptions"
            :multiple="true"
            :close-on-select="false"
            :show-labels="false"
            label="label"
            track-by="value"
            placeholder="Any server"
            @input="onFilterChange"
          >
            <template slot="selection" slot-scope="{ values }">
              <span v-if="values.length" class="osl-ms__summary">{{ summarizeSelection(values) }}</span>
            </template>
          </multiselect>
        </div>

        <div class="osl-field osl-field--dates">
          <label class="osl-field__label">Date range</label>
          <div class="osl-daterange">
            <input v-model="filters.from_date" class="osl-input" type="date" :max="filters.to_date || today" @change="onFilterChange" />
            <span class="osl-muted">→</span>
            <input v-model="filters.to_date" class="osl-input" type="date" :min="filters.from_date" :max="today" @change="onFilterChange" />
          </div>
        </div>

        <div class="osl-field">
          <label class="osl-field__label">Resend state</label>
          <select v-model="filters.resend_status" class="osl-input osl-select" @change="onFilterChange">
            <option value="">Any</option>
            <option value="None">Never resent</option>
            <option value="Queued,Processing">In progress</option>
            <option value="Done">Resent OK</option>
            <option value="Failed">Resend failed</option>
          </select>
        </div>

        <div class="osl-field">
          <label class="osl-field__label">Trigger</label>
          <select v-model="filters.trigger_source" class="osl-input osl-select" @change="onFilterChange">
            <option value="">Any</option>
            <option value="AUTO">Auto (original call)</option>
            <option value="RESEND">Re-run</option>
            <option value="REPLAY">Replay</option>
          </select>
        </div>
      </div>

      <div class="osl-filters__footer">
        <div class="osl-quick">
          <span class="osl-muted osl-small mr-1">Quick:</span>
          <button
            v-for="range in quickRanges"
            :key="range.key"
            :class="['osl-chip', { 'is-active': activeQuickRange === range.key }]"
            @click="applyQuickRange(range)"
          >
            {{ range.label }}
          </button>
        </div>
        <div class="osl-filters__right">
          <label class="osl-check osl-small" title="Also show earlier attempts that have since been resent">
            <input v-model="filters.include_history" type="checkbox" @change="onFilterChange" />
            Show earlier attempts
          </label>
          <button v-if="activeFilterCount" class="osl-btn osl-btn--link osl-btn--sm" @click="resetFilters">
            <i class="fa fa-times-circle"></i> Clear {{ activeFilterCount }} filter{{ activeFilterCount > 1 ? "s" : "" }}
          </button>
        </div>
      </div>
    </div>

    <!-- ───────────── Bulk bar ───────────── -->
    <transition name="osl-fade">
      <div v-if="selectedCount" class="osl-bulkbar">
        <div>
          <b>{{ selectedCount }}</b> selected
          <span class="osl-muted osl-small ml-1" v-if="selectedSyncedCount">
            ({{ selectedSyncedCount }} already synced)
          </span>
        </div>
        <div class="osl-bulkbar__actions">
          <button class="osl-btn osl-btn--primary osl-btn--sm" @click="openResend(selectedRows, 'RERUN')">
            <i class="fa fa-refresh"></i> Re-run
          </button>
          <button class="osl-btn osl-btn--secondary osl-btn--sm" @click="openResend(selectedRows, 'REPLAY')">
            <i class="fa fa-repeat"></i> Replay
          </button>
          <button class="osl-btn osl-btn--ghost osl-btn--sm" @click="clearSelection">Clear</button>
        </div>
      </div>
    </transition>

    <!-- ───────────── Table ───────────── -->
    <div class="osl-card osl-table-card">
      <div class="osl-table-wrap">
        <table class="osl-table">
          <thead>
            <tr>
              <th class="osl-col-check">
                <input
                  type="checkbox"
                  :checked="allPageSelected"
                  :indeterminate.prop="somePageSelected && !allPageSelected"
                  :disabled="!selectableRows.length"
                  title="Select all resendable rows on this page"
                  @change="togglePage($event.target.checked)"
                />
              </th>
              <th>Time</th>
              <th>Job</th>
              <th>Reference</th>
              <th>Cinema</th>
              <th title="Server that made the call">Server</th>
              <th>Endpoint</th>
              <th class="text-right">HTTP</th>
              <th class="text-right">Took</th>
              <th>Status</th>
              <th class="osl-col-error">Error</th>
              <th>Attempt</th>
              <th>Resend</th>
              <th class="osl-col-actions"></th>
            </tr>
          </thead>
          <tbody v-if="loading && !logs.length">
            <tr v-for="n in 8" :key="'sk' + n" class="osl-row--skeleton">
              <td v-for="c in 14" :key="c"><span class="osl-skeleton"></span></td>
            </tr>
          </tbody>
          <tbody v-else-if="!logs.length">
            <tr>
              <td colspan="14">
                <div class="osl-empty">
                  <i class="fa fa-inbox"></i>
                  <div class="osl-empty__title">{{ loadError ? "Couldn't load logs" : "No logs match these filters" }}</div>
                  <div class="osl-muted osl-small">{{ loadError || "Try widening the date range or clearing filters." }}</div>
                  <button v-if="activeFilterCount && !loadError" class="osl-btn osl-btn--ghost osl-btn--sm mt-2" @click="resetFilters">Clear filters</button>
                  <button v-if="loadError" class="osl-btn osl-btn--ghost osl-btn--sm mt-2" @click="refreshAll()">Try again</button>
                </div>
              </td>
            </tr>
          </tbody>
          <tbody v-else :class="{ 'is-refreshing': loading }">
            <tr
              v-for="row in logs"
              :key="row.request_uid"
              :class="[
                'osl-row',
                'osl-row--' + String(row.status).toLowerCase(),
                { 'is-selected': !!selected[row.request_uid], 'is-open': drawer.uid === row.request_uid, 'is-superseded': row.is_superseded === 'Y' },
              ]"
              @click="openDrawer(row)"
            >
              <td class="osl-col-check" @click.stop>
                <input
                  type="checkbox"
                  :checked="!!selected[row.request_uid]"
                  :disabled="!!rowBlockedReason(row)"
                  :title="rowBlockedReason(row) || 'Select'"
                  @change="toggleRow(row, $event.target.checked)"
                />
              </td>
              <td class="osl-nowrap" :title="fmtFull(row.created_at)">
                <div class="osl-cell-main">{{ fmtTime(row.created_at) }}</div>
                <div class="osl-cell-sub">{{ fmtDate(row.created_at) }}</div>
              </td>
              <td class="osl-nowrap">
                <div class="osl-cell-main">{{ jobMeta(row.job_type).label }}</div>
                <div class="osl-cell-sub">
                  <span :class="['osl-action', 'osl-action--' + String(jobMeta(row.job_type).action).toLowerCase()]">{{ jobMeta(row.job_type).action }}</span>
                  <span class="osl-mono" :title="row.job_type">{{ row.job_type }}</span>
                </div>
              </td>
              <td class="osl-mono osl-cell-ref" :title="row.reference_id">{{ row.reference_id || "—" }}</td>
              <td class="osl-nowrap">{{ cinemaLabel(row.cinema_id, row.cinema_name) }}</td>
              <td class="osl-nowrap">
                <span :class="['osl-tag', Number(row.source_cinema_id) === 0 ? 'osl-tag--blue' : 'osl-tag--violet']">
                  {{ sourceShort(row.source_cinema_id) }}
                </span>
              </td>
              <td class="osl-mono osl-cell-endpoint" :title="row.http_method + ' ' + row.endpoint">
                <span class="osl-method">{{ row.http_method }}</span>{{ row.endpoint }}
              </td>
              <td class="text-right">
                <span :class="['osl-http', httpClass(row.http_status)]">{{ row.http_status || "—" }}</span>
              </td>
              <td class="text-right osl-nowrap osl-muted">{{ formatDuration(row.duration_ms) }}</td>
              <td class="osl-nowrap">
                <span :class="['osl-badge', statusMeta(row.status).cls]">{{ statusMeta(row.status).label }}</span>
                <span v-if="row.is_duplicate === 'Y'" class="osl-badge osl-badge--neutral" title="Odoo reported a duplicate. Treated as synced.">Dup</span>
              </td>
              <td class="osl-col-error">
                <div v-if="row.error_message && row.status !== 'Completed'" class="osl-error-text" :title="row.error_message">
                  {{ row.error_message }}
                </div>
                <span v-else class="osl-muted">—</span>
              </td>
              <td class="osl-nowrap">
                <span :class="triggerMeta(row.trigger_source).cls">{{ triggerMeta(row.trigger_source).label }}</span>
                <span v-if="Number(row.retry_count) > 0" class="osl-muted osl-small">#{{ Number(row.retry_count) + 1 }}</span>
              </td>
              <td class="osl-nowrap">
                <span
                  v-if="row.resend_status && row.resend_status !== 'None'"
                  :class="['osl-badge', resendMeta(row.resend_status).cls]"
                  :title="resendTitle(row)"
                >{{ resendMeta(row.resend_status).label }}</span>
                <span v-else class="osl-muted">—</span>
              </td>
              <td class="osl-col-actions" @click.stop>
                <div class="osl-row-actions">
                  <button class="osl-icon-btn" title="View details" @click="openDrawer(row)"><i class="fa fa-eye"></i></button>
                  <button
                    class="osl-icon-btn osl-icon-btn--primary"
                    :disabled="!!rowBlockedReason(row, 'RERUN')"
                    :title="rowBlockedReason(row, 'RERUN') || 'Re-run job'"
                    @click="openResend([row], 'RERUN')"
                  ><i class="fa fa-refresh"></i></button>
                  <button
                    class="osl-icon-btn"
                    :disabled="!!rowBlockedReason(row, 'REPLAY')"
                    :title="rowBlockedReason(row, 'REPLAY') || 'Replay exact request'"
                    @click="openResend([row], 'REPLAY')"
                  ><i class="fa fa-repeat"></i></button>
                </div>
              </td>
            </tr>
          </tbody>
        </table>
      </div>

      <!-- Pagination -->
      <div class="osl-pager">
        <div class="osl-muted osl-small">
          <template v-if="pagination.total">
            Showing <b>{{ formatNumber(rangeStart) }}–{{ formatNumber(rangeEnd) }}</b> of <b>{{ formatNumber(pagination.total) }}</b>
          </template>
          <template v-else>0 results</template>
        </div>
        <div class="osl-pager__right">
          <label class="osl-small osl-muted mr-1">Rows</label>
          <select v-model.number="pageSize" class="osl-input osl-select osl-select--xs" @change="goToPage(1)">
            <option v-for="size in [20, 50, 100, 200]" :key="size" :value="size">{{ size }}</option>
          </select>
          <button class="osl-pager__btn" :disabled="pagination.currentPage <= 1" title="First" @click="goToPage(1)"><i class="fa fa-angle-double-left"></i></button>
          <button class="osl-pager__btn" :disabled="pagination.currentPage <= 1" title="Previous" @click="goToPage(pagination.currentPage - 1)"><i class="fa fa-angle-left"></i></button>
          <button
            v-for="p in pageNumbers"
            :key="'p' + p.key"
            :class="['osl-pager__btn', { 'is-active': p.value === pagination.currentPage }]"
            :disabled="p.value === null"
            @click="p.value && goToPage(p.value)"
          >{{ p.value === null ? "…" : p.value }}</button>
          <button class="osl-pager__btn" :disabled="pagination.currentPage >= pagination.lastPage" title="Next" @click="goToPage(pagination.currentPage + 1)"><i class="fa fa-angle-right"></i></button>
          <button class="osl-pager__btn" :disabled="pagination.currentPage >= pagination.lastPage" title="Last" @click="goToPage(pagination.lastPage)"><i class="fa fa-angle-double-right"></i></button>
        </div>
      </div>
    </div>

    <!-- ───────────── Detail drawer ───────────── -->
    <odoo-log-detail-drawer
      :visible="drawer.visible"
      :request-uid="drawer.uid"
      :cinema-map="cinemaMap"
      :resend-supported-types="resendSupportedTypes"
      :refresh-key="drawerRefreshKey"
      @close="closeDrawer"
      @open="openDrawerByUid"
      @resend="(e) => openResend(e.rows, e.mode)"
    />

    <!-- ───────────── Resend confirm modal ───────────── -->
    <b-modal
      id="osl-resend-modal"
      v-model="resend.visible"
      :title="resendModalTitle"
      size="lg"
      centered
      no-close-on-backdrop
      modal-class="osl-modal"
      @hidden="onResendHidden"
    >
      <!-- Result state -->
      <div v-if="resend.results">
        <div :class="['osl-callout', resend.queuedCount ? 'osl-callout--success' : 'osl-callout--warning']">
          <i :class="['fa', resend.queuedCount ? 'fa-check-circle' : 'fa-exclamation-triangle']"></i>
          <div>
            <div class="osl-callout__title">{{ resend.resultMessage }}</div>
            <div class="osl-callout__text">
              Rows owned by this server start right away. Rows from cinema servers are picked up by that server within about 2 minutes. Progress shows in the <b>Resend</b> column.
            </div>
          </div>
        </div>
        <div v-if="resend.skippedResults.length" class="osl-result-list">
          <div class="osl-result-list__title">Not queued ({{ resend.skippedResults.length }})</div>
          <div v-for="r in resend.skippedResults" :key="r.request_uid" class="osl-result-list__row">
            <span class="osl-mono osl-small">{{ r.request_uid }}</span>
            <span class="osl-small">{{ r.message }}</span>
          </div>
        </div>
      </div>

      <!-- Confirm state -->
      <div v-else>
        <div class="osl-mode-cards">
          <label :class="['osl-mode-card', { 'is-active': resend.mode === 'RERUN' }]">
            <input v-model="resend.mode" type="radio" value="RERUN" />
            <div>
              <div class="osl-mode-card__title"><i class="fa fa-refresh"></i> Re-run job <span class="osl-tag osl-tag--blue ml-1">Recommended</span></div>
              <div class="osl-mode-card__text">{{ modeHelp.RERUN }}</div>
            </div>
          </label>
          <label :class="['osl-mode-card', { 'is-active': resend.mode === 'REPLAY' }]">
            <input v-model="resend.mode" type="radio" value="REPLAY" />
            <div>
              <div class="osl-mode-card__title"><i class="fa fa-repeat"></i> Replay request</div>
              <div class="osl-mode-card__text">{{ modeHelp.REPLAY }}</div>
            </div>
          </label>
        </div>

        <div class="osl-resend-summary">
          <div class="osl-resend-summary__item">
            <div class="osl-resend-summary__num">{{ resendPlan.eligible.length }}</div>
            <div class="osl-muted osl-small">will be sent</div>
          </div>
          <div class="osl-resend-summary__item">
            <div class="osl-resend-summary__num">{{ resendPlan.onThisServer }}</div>
            <div class="osl-muted osl-small">run on this server now</div>
          </div>
          <div class="osl-resend-summary__item">
            <div class="osl-resend-summary__num">{{ resendPlan.onOtherServers }}</div>
            <div class="osl-muted osl-small">queued for cinema servers</div>
          </div>
          <div class="osl-resend-summary__item">
            <div class="osl-resend-summary__num">{{ resendPlan.blocked.length }}</div>
            <div class="osl-muted osl-small">skipped</div>
          </div>
        </div>

        <label v-if="resendPlan.synced.length" class="osl-check osl-force">
          <input v-model="resend.force" type="checkbox" />
          <span>
            Also resend <b>{{ resendPlan.synced.length }}</b> log{{ resendPlan.synced.length > 1 ? "s" : "" }} that already synced.
            <span class="osl-muted">Odoo may create a duplicate record unless the endpoint handles duplicates.</span>
          </span>
        </label>

        <div v-if="resend.mode === 'REPLAY'" class="osl-callout osl-callout--warning">
          <i class="fa fa-exclamation-triangle"></i>
          <div class="osl-callout__text">
            Replay sends the payload exactly as it was stored. If the source data changed since then (prices, items, partner), Odoo gets the old values.
          </div>
        </div>

        <div v-if="resendPlan.blocked.length" class="osl-result-list">
          <div class="osl-result-list__title">Skipped ({{ resendPlan.blocked.length }})</div>
          <div v-for="b in resendPlan.blocked.slice(0, 8)" :key="b.row.request_uid" class="osl-result-list__row">
            <span class="osl-small">{{ jobMeta(b.row.job_type).label }} · <span class="osl-mono">{{ b.row.reference_id || b.row.request_uid }}</span></span>
            <span class="osl-small osl-muted">{{ b.reason }}</span>
          </div>
          <div v-if="resendPlan.blocked.length > 8" class="osl-muted osl-small mt-1">and {{ resendPlan.blocked.length - 8 }} more</div>
        </div>
      </div>

      <template slot="modal-footer">
        <template v-if="resend.results">
          <button class="osl-btn osl-btn--primary" @click="resend.visible = false">Done</button>
        </template>
        <template v-else>
          <button class="osl-btn osl-btn--ghost" :disabled="resend.submitting" @click="resend.visible = false">Cancel</button>
          <button
            class="osl-btn osl-btn--primary"
            :disabled="resend.submitting || !resendPlan.eligible.length"
            @click="submitResend"
          >
            <i v-if="resend.submitting" class="fa fa-spinner fa-spin"></i>
            {{ resend.mode === "REPLAY" ? "Replay" : "Re-run" }} {{ resendPlan.eligible.length }} log{{ resendPlan.eligible.length === 1 ? "" : "s" }}
          </button>
        </template>
      </template>
    </b-modal>
  </div>
</template>

<script>
import moment from "moment";
import OdooLogsService from "@/services/OdooLogsService";
import MasterService from "@/services/MasterService";
import OdooLogDetailDrawer from "./odooLogDetailDrawer.vue";
import {
  JOB_TYPE_LABELS,
  jobTypeMeta,
  STATUS_META,
  RESEND_STATUS_META,
  TRIGGER_META,
  RESEND_MODE_HELP,
  formatDuration,
} from "./odooLogsMeta";

const DEFAULT_RANGE_DAYS = 7;
const FAST_POLL_MS = 5000;
const AUTO_REFRESH_MS = 30000;
const SEARCH_DEBOUNCE_MS = 400;

const STATUS_OPTIONS = [
  { value: "Failed", label: "Failed" },
  { value: "Pending", label: "Pending" },
  { value: "Completed", label: "Synced" },
];

const defaultFilters = () => ({
  search: "",
  status: [],
  job_type: [],
  cinema_id: [],
  source_cinema_id: [],
  resend_status: "",
  trigger_source: "",
  from_date: moment().subtract(DEFAULT_RANGE_DAYS - 1, "days").format("YYYY-MM-DD"),
  to_date: moment().format("YYYY-MM-DD"),
  include_history: false,
});

export default {
  name: "OdooSyncLogs",
  components: { OdooLogDetailDrawer },
  data() {
    return {
      logs: [],
      loading: false,
      loadError: null,
      summary: { counts: { Pending: 0, Failed: 0, Completed: 0 }, not_synced: 0, resend_in_progress: 0, job_types: [], resend_supported_job_types: [] },
      summaryLoading: false,
      summaryLoaded: false,
      pagination: { total: 0, perPage: 50, currentPage: 1, lastPage: 1 },
      pageSize: 50,
      filters: defaultFilters(),
      searchInput: "",
      searchTimer: null,
      cinemas: [],
      selected: {},
      drawer: { visible: false, uid: null },
      drawerRefreshKey: 0,
      autoRefresh: false,
      lastUpdated: null,
      nowTick: Date.now(),
      timers: { poll: null, clock: null },
      requestSeq: 0,
      statusOptions: STATUS_OPTIONS,
      modeHelp: RESEND_MODE_HELP,
      resend: {
        visible: false,
        mode: "RERUN",
        rows: [],
        force: false,
        submitting: false,
        results: null,
        queuedCount: 0,
        resultMessage: "",
        skippedResults: [],
      },
      quickRanges: [
        { key: "today", label: "Today", days: 1 },
        { key: "7d", label: "7 days", days: 7 },
        { key: "30d", label: "30 days", days: 30 },
        { key: "90d", label: "90 days", days: 90 },
        { key: "all", label: "All time", days: null },
      ],
    };
  },
  computed: {
    today() {
      return moment().format("YYYY-MM-DD");
    },
    isLocalDashboard() {
      return !!process.env.CINEMA_SOURCE && String(process.env.CINEMA_SOURCE) !== "0";
    },
    currentServerId() {
      return Number(process.env.CINEMA_SOURCE || 0);
    },
    cinemaMap() {
      const map = {};
      this.cinemas.forEach((c) => {
        map[c.cine_id] = c.cine_name;
      });
      return map;
    },
    cinemaOptions() {
      return this.cinemas.map((c) => ({ value: c.cine_id, label: c.cine_name }));
    },
    sourceOptions() {
      return [{ value: 0, label: "Cloud server" }].concat(
        this.cinemas.map((c) => ({ value: c.cine_id, label: `${c.cine_name} server` })),
      );
    },
    resendSupportedTypes() {
      return this.summary.resend_supported_job_types || [];
    },
    jobTypeOptions() {
      const types = new Set(Object.keys(JOB_TYPE_LABELS).concat(this.summary.job_types || []));
      const groups = {};
      types.forEach((type) => {
        const meta = jobTypeMeta(type);
        const label = meta.action ? `${meta.label} · ${meta.action}` : meta.label;
        if (!groups[meta.group]) groups[meta.group] = [];
        groups[meta.group].push({ value: type, label });
      });
      const order = ["Sales", "Purchase", "Master", "Other"];
      return Object.keys(groups)
        .sort((a, b) => order.indexOf(a) - order.indexOf(b))
        .map((group) => ({
          group,
          items: groups[group].sort((a, b) => a.label.localeCompare(b.label)),
        }));
    },
    kpis() {
      const c = this.summary.counts || {};
      const total = (c.Pending || 0) + (c.Failed || 0) + (c.Completed || 0);
      return [
        { key: "not_synced", label: "Not synced", value: this.summary.not_synced || 0, tone: "danger", icon: "fa-exclamation-circle", status: ["Failed", "Pending"], hint: "Failed + Pending: needs attention" },
        { key: "failed", label: "Failed", value: c.Failed || 0, tone: "danger-soft", icon: "fa-times-circle", status: ["Failed"], hint: "Odoo returned an error or the call failed" },
        { key: "pending", label: "Pending", value: c.Pending || 0, tone: "warning", icon: "fa-clock-o", status: ["Pending"], hint: "No response recorded yet. If this persists, the call was likely interrupted." },
        { key: "completed", label: "Synced", value: c.Completed || 0, tone: "success", icon: "fa-check-circle", status: ["Completed"], hint: "Accepted by Odoo (includes duplicates)" },
        { key: "resending", label: "Resend in progress", value: this.summary.resend_in_progress || 0, tone: "info", icon: "fa-spinner", resend: "Queued,Processing", hint: "Queued or processing" },
        { key: "all", label: "Total", value: total, tone: "neutral", icon: "fa-list", status: [], hint: "All latest attempts in range" },
      ];
    },
    activeKpi() {
      const statuses = this.filters.status.map((s) => s.value).sort().join(",");
      if (this.filters.resend_status === "Queued,Processing" && !statuses) return "resending";
      if (this.filters.resend_status) return null;
      if (statuses === "Failed,Pending") return "not_synced";
      if (statuses === "Failed") return "failed";
      if (statuses === "Pending") return "pending";
      if (statuses === "Completed") return "completed";
      if (!statuses) return "all";
      return null;
    },
    activeQuickRange() {
      const { from_date, to_date } = this.filters;
      if (!from_date && !to_date) return "all";
      if (to_date !== this.today) return null;
      const match = this.quickRanges.find(
        (r) => r.days && from_date === moment().subtract(r.days - 1, "days").format("YYYY-MM-DD"),
      );
      return match ? match.key : null;
    },
    activeFilterCount() {
      const f = this.filters;
      const d = defaultFilters();
      let n = 0;
      if (f.search) n++;
      if (f.status.length) n++;
      if (f.job_type.length) n++;
      if (f.cinema_id.length) n++;
      if (f.source_cinema_id.length) n++;
      if (f.resend_status) n++;
      if (f.trigger_source) n++;
      if (f.include_history) n++;
      if (f.from_date !== d.from_date || f.to_date !== d.to_date) n++;
      return n;
    },
    selectedRows() {
      return Object.keys(this.selected).map((k) => this.selected[k]);
    },
    selectedCount() {
      return this.selectedRows.length;
    },
    selectedSyncedCount() {
      return this.selectedRows.filter((r) => r.status === "Completed").length;
    },
    selectableRows() {
      return this.logs.filter((r) => !this.rowBlockedReason(r));
    },
    allPageSelected() {
      return this.selectableRows.length > 0 && this.selectableRows.every((r) => this.selected[r.request_uid]);
    },
    somePageSelected() {
      return this.selectableRows.some((r) => this.selected[r.request_uid]);
    },
    hasActiveResends() {
      return (
        (this.summary.resend_in_progress || 0) > 0 ||
        this.logs.some((r) => r.resend_status === "Queued" || r.resend_status === "Processing")
      );
    },
    rangeStart() {
      if (!this.pagination.total) return 0;
      return (this.pagination.currentPage - 1) * this.pagination.perPage + 1;
    },
    rangeEnd() {
      return Math.min(this.pagination.currentPage * this.pagination.perPage, this.pagination.total);
    },
    pageNumbers() {
      const current = this.pagination.currentPage;
      const last = this.pagination.lastPage;
      const pages = [];
      const add = (value) => pages.push({ key: value === null ? `gap${pages.length}` : value, value });
      const windowStart = Math.max(2, current - 1);
      const windowEnd = Math.min(last - 1, current + 1);
      add(1);
      if (windowStart > 2) add(null);
      for (let i = windowStart; i <= windowEnd; i++) add(i);
      if (windowEnd < last - 1) add(null);
      if (last > 1) add(last);
      return pages;
    },
    lastUpdatedLabel() {
      if (!this.lastUpdated) return "Not loaded yet";
      const secs = Math.max(0, Math.round((this.nowTick - this.lastUpdated) / 1000));
      if (secs < 5) return "Updated just now";
      if (secs < 60) return `Updated ${secs}s ago`;
      return `Updated ${moment(this.lastUpdated).fromNow()}`;
    },
    lastUpdatedExact() {
      return this.lastUpdated ? moment(this.lastUpdated).format("DD MMM YYYY, HH:mm:ss") : "";
    },
    resendModalTitle() {
      if (this.resend.results) return "Resend queued";
      const n = this.resend.rows.length;
      return `${this.resend.mode === "REPLAY" ? "Replay" : "Re-run"} ${n} log${n === 1 ? "" : "s"}`;
    },
    resendPlan() {
      const eligible = [];
      const blocked = [];
      const synced = [];
      this.resend.rows.forEach((row) => {
        const reason = this.rowBlockedReason(row, this.resend.mode);
        if (reason) {
          blocked.push({ row, reason });
          return;
        }
        if (row.status === "Completed") {
          synced.push(row);
          if (!this.resend.force) {
            blocked.push({ row, reason: "Already synced (tick the box above to include it)" });
            return;
          }
        }
        eligible.push(row);
      });
      const onThisServer = eligible.filter((r) => Number(r.source_cinema_id || 0) === this.currentServerId).length;
      return {
        eligible,
        blocked,
        synced,
        onThisServer,
        onOtherServers: eligible.length - onThisServer,
      };
    },
  },
  watch: {
    searchInput(val) {
      clearTimeout(this.searchTimer);
      this.searchTimer = setTimeout(() => {
        if (val !== this.filters.search) {
          this.filters.search = val.trim();
          this.onFilterChange();
        }
      }, SEARCH_DEBOUNCE_MS);
    },
    autoRefresh() {
      this.schedulePoll();
    },
    hasActiveResends() {
      this.schedulePoll();
    },
  },
  created() {
    this.restoreFromQuery();
  },
  mounted() {
    this.loadCinemas();
    this.refreshAll();
    this.timers.clock = setInterval(() => {
      this.nowTick = Date.now();
    }, 5000);
  },
  beforeDestroy() {
    clearTimeout(this.timers.poll);
    clearInterval(this.timers.clock);
    clearTimeout(this.searchTimer);
  },
  methods: {
    formatDuration,
    jobMeta: jobTypeMeta,
    statusMeta(status) {
      return STATUS_META[status] || { label: status || "—", cls: "osl-badge--neutral" };
    },
    resendMeta(status) {
      return RESEND_STATUS_META[status] || { label: status, cls: "osl-badge--neutral" };
    },
    triggerMeta(trigger) {
      return TRIGGER_META[trigger] || TRIGGER_META.AUTO;
    },
    formatNumber(n) {
      return Number(n || 0).toLocaleString();
    },
    fmtTime(d) {
      return d ? moment(d).format("HH:mm:ss") : "—";
    },
    fmtDate(d) {
      if (!d) return "";
      const m = moment(d);
      if (m.isSame(moment(), "day")) return "Today";
      if (m.isSame(moment().subtract(1, "day"), "day")) return "Yesterday";
      return m.format("DD MMM YY");
    },
    fmtFull(d) {
      return d ? `${moment(d).format("DD MMM YYYY, HH:mm:ss")} (${moment(d).fromNow()})` : "";
    },
    cinemaLabel(id, name) {
      if (id === null || id === undefined) return "—";
      if (Number(id) === 0) return "Cloud / HO";
      return name || this.cinemaMap[id] || `Cinema #${id}`;
    },
    sourceShort(id) {
      if (id === null || id === undefined || Number(id) === 0) return "Cloud";
      return this.cinemaMap[id] || `Cinema #${id}`;
    },
    httpClass(code) {
      if (!code) return "is-none";
      if (code >= 200 && code < 300) return "is-ok";
      if (code >= 400 && code < 500) return "is-warn";
      return "is-err";
    },
    resendTitle(row) {
      const parts = [`Mode: ${row.resend_mode || "—"}`];
      if (row.resend_requested_at) parts.push(`Requested: ${moment(row.resend_requested_at).format("DD MMM, HH:mm")}`);
      if (row.resend_requested_by_name) parts.push(`By: ${row.resend_requested_by_name}`);
      if (row.resend_error) parts.push(`Error: ${row.resend_error}`);
      return parts.join("\n");
    },
    summarizeSelection(values) {
      if (values.length === 1) return values[0].label;
      return `${values.length} selected`;
    },
    rowBlockedReason(row, mode = "RERUN") {
      if (row.is_superseded === "Y") return "A newer attempt exists. Resend the latest one.";
      if (row.resend_status === "Queued" || row.resend_status === "Processing")
        return `Resend already ${String(row.resend_status).toLowerCase()}`;
      if (mode === "RERUN" && this.resendSupportedTypes.length && this.resendSupportedTypes.indexOf(row.job_type) === -1)
        return "Re-run isn't supported for this job type";
      if (mode === "REPLAY" && !row.endpoint) return "No stored request to replay";
      return null;
    },

    // ── Query params ──
    buildParams() {
      const f = this.filters;
      const join = (arr) => arr.map((o) => o.value).join(",");
      return {
        search: f.search,
        status: join(f.status),
        job_type: join(f.job_type),
        cinema_id: join(f.cinema_id),
        source_cinema_id: join(f.source_cinema_id),
        resend_status: f.resend_status,
        trigger_source: f.trigger_source,
        from_date: f.from_date,
        to_date: f.to_date,
        include_history: f.include_history ? "Y" : "N",
      };
    },
    syncQuery() {
      const params = this.buildParams();
      const query = {};
      Object.keys(params).forEach((k) => {
        if (params[k] && !(k === "include_history" && params[k] === "N")) query[k] = String(params[k]);
      });
      if (!params.from_date && !params.to_date) query.range = "all";
      if (this.pagination.currentPage > 1) query.page = String(this.pagination.currentPage);
      if (this.pageSize !== 50) query.size = String(this.pageSize);
      if (this.drawer.uid) query.log = this.drawer.uid;
      if (JSON.stringify(query) === JSON.stringify(this.$route.query)) return;
      this.$router.replace({ query }).catch(() => {});
    },
    restoreFromQuery() {
      const q = (this.$route && this.$route.query) || {};
      if (!Object.keys(q).length) return;
      const split = (v) => (v ? String(v).split(",").filter(Boolean) : []);
      const f = defaultFilters();
      f.search = q.search || "";
      f.status = split(q.status).map((v) => STATUS_OPTIONS.find((o) => o.value === v) || { value: v, label: v });
      f.job_type = split(q.job_type).map((v) => {
        const meta = jobTypeMeta(v);
        return { value: v, label: meta.action ? `${meta.label} · ${meta.action}` : meta.label };
      });
      // labels are filled in once cinemas load
      f.cinema_id = split(q.cinema_id).map((v) => ({ value: Number(v), label: `Cinema #${v}` }));
      f.source_cinema_id = split(q.source_cinema_id).map((v) => ({ value: Number(v), label: Number(v) === 0 ? "Cloud server" : `Cinema #${v} server` }));
      f.resend_status = q.resend_status || "";
      f.trigger_source = q.trigger_source || "";
      if (q.range === "all") {
        f.from_date = "";
        f.to_date = "";
      } else {
        if (q.from_date) f.from_date = q.from_date;
        if (q.to_date) f.to_date = q.to_date;
      }
      f.include_history = q.include_history === "Y";
      this.filters = f;
      this.searchInput = f.search;
      if (q.size) this.pageSize = Number(q.size) || 50;
      if (q.page) this.pagination.currentPage = Number(q.page) || 1;
      if (q.log) this.drawer = { visible: true, uid: q.log };
    },

    // ── Data ──
    loadCinemas() {
      MasterService.GetCinemasByRoles()
        .then(({ data }) => {
          if (data && data.status && data.Records) {
            this.cinemas = data.Records;
            // refresh labels of filters restored from the URL
            const fix = (arr, suffix) =>
              arr.map((o) => (this.cinemaMap[o.value] ? { value: o.value, label: this.cinemaMap[o.value] + suffix } : o));
            this.filters.cinema_id = fix(this.filters.cinema_id, "");
            this.filters.source_cinema_id = fix(this.filters.source_cinema_id, " server");
          }
        })
        .catch(() => {});
    },
    refreshAll(silent = false) {
      this.loadLogs(silent);
      this.loadSummary(silent);
      if (this.drawer.visible) this.drawerRefreshKey++;
    },
    loadLogs(silent = false) {
      const seq = ++this.requestSeq;
      if (!silent) this.loading = true;
      const params = {
        ...this.buildParams(),
        limit: this.pageSize,
        currentPage: this.pagination.currentPage,
      };
      OdooLogsService.GetOdooApiLogs(params)
        .then(({ data }) => {
          if (seq !== this.requestSeq) return; // a newer request is in flight
          if (data && data.status) {
            this.logs = data.Records || [];
            this.pagination = data.pagination || this.pagination;
            this.loadError = null;
            this.lastUpdated = Date.now();
            this.nowTick = Date.now();
            // keep selections in sync with fresh row data, drop rows that are no longer resendable
            const next = {};
            this.logs.forEach((r) => {
              if (this.selected[r.request_uid] && !this.rowBlockedReason(r)) next[r.request_uid] = r;
            });
            this.selected = next;
            if (this.pagination.currentPage > this.pagination.lastPage && this.pagination.lastPage >= 1) {
              this.goToPage(this.pagination.lastPage);
            }
          } else {
            this.loadError = (data && (data.message || data.error)) || "Failed to load logs";
            if (!silent) this.logs = [];
          }
        })
        .catch((err) => {
          if (seq !== this.requestSeq) return;
          this.loadError = (err && err.message) || "Failed to load logs";
          if (!silent) this.logs = [];
        })
        .finally(() => {
          if (seq === this.requestSeq) this.loading = false;
          this.schedulePoll();
        });
    },
    loadSummary(silent = false) {
      if (!silent) this.summaryLoading = true;
      const params = this.buildParams();
      delete params.status;
      OdooLogsService.GetOdooApiLogsSummary(params)
        .then(({ data }) => {
          if (data && data.status && data.Records) {
            this.summary = data.Records;
            this.summaryLoaded = true;
          }
        })
        .catch(() => {})
        .finally(() => {
          this.summaryLoading = false;
        });
    },
    schedulePoll() {
      clearTimeout(this.timers.poll);
      let delay = null;
      if (this.hasActiveResends) delay = FAST_POLL_MS;
      else if (this.autoRefresh) delay = AUTO_REFRESH_MS;
      if (!delay) return;
      this.timers.poll = setTimeout(() => {
        if (document.hidden) return this.schedulePoll();
        this.refreshAll(true);
      }, delay);
    },

    // ── Filters ──
    onFilterChange() {
      this.pagination.currentPage = 1;
      this.clearSelection();
      this.syncQuery();
      this.refreshAll();
    },
    commitSearch() {
      clearTimeout(this.searchTimer);
      this.filters.search = this.searchInput.trim();
      this.onFilterChange();
    },
    clearSearch() {
      this.searchInput = "";
      this.commitSearch();
    },
    resetFilters() {
      this.filters = defaultFilters();
      this.searchInput = "";
      this.onFilterChange();
    },
    applyKpi(kpi) {
      if (kpi.resend) {
        this.filters.status = [];
        this.filters.resend_status = kpi.resend;
      } else {
        this.filters.resend_status = "";
        this.filters.status = kpi.status.map((s) => STATUS_OPTIONS.find((o) => o.value === s));
      }
      this.onFilterChange();
    },
    applyQuickRange(range) {
      if (!range.days) {
        this.filters.from_date = "";
        this.filters.to_date = "";
      } else {
        this.filters.from_date = moment().subtract(range.days - 1, "days").format("YYYY-MM-DD");
        this.filters.to_date = this.today;
      }
      this.onFilterChange();
    },
    goToPage(page) {
      const target = Math.max(1, Math.min(page, this.pagination.lastPage || 1));
      this.pagination.currentPage = target;
      this.pagination.perPage = this.pageSize;
      this.clearSelection();
      this.syncQuery();
      this.loadLogs();
    },

    // ── Selection ──
    toggleRow(row, checked) {
      const next = { ...this.selected };
      if (checked) next[row.request_uid] = row;
      else delete next[row.request_uid];
      this.selected = next;
    },
    togglePage(checked) {
      const next = { ...this.selected };
      this.selectableRows.forEach((r) => {
        if (checked) next[r.request_uid] = r;
        else delete next[r.request_uid];
      });
      this.selected = next;
    },
    clearSelection() {
      this.selected = {};
    },

    // ── Drawer ──
    openDrawer(row) {
      this.openDrawerByUid(row.request_uid);
    },
    openDrawerByUid(uid) {
      this.drawer = { visible: true, uid };
      this.syncQuery();
    },
    closeDrawer() {
      this.drawer = { visible: false, uid: null };
      this.syncQuery();
    },

    // ── Resend ──
    openResend(rows, mode) {
      this.resend = {
        visible: true,
        mode,
        rows: rows.slice(),
        force: false,
        submitting: false,
        results: null,
        queuedCount: 0,
        resultMessage: "",
        skippedResults: [],
      };
    },
    submitResend() {
      const eligible = this.resendPlan.eligible;
      if (!eligible.length) return;
      this.resend.submitting = true;
      OdooLogsService.ResendOdooApiLogs({
        request_uids: eligible.map((r) => r.request_uid),
        mode: this.resend.mode,
        force: this.resend.force,
      })
        .then(({ data }) => {
          const results = (data && data.Records) || [];
          const queued = results.filter((r) => r.queued);
          const skipped = results.filter((r) => !r.queued);
          this.resend.queuedCount = queued.length;
          this.resend.resultMessage = (data && data.message) || `${queued.length} log(s) queued`;
          this.resend.skippedResults = skipped;
          if (!skipped.length && queued.length) {
            this.$toasted.show(this.resend.resultMessage);
            this.resend.visible = false;
          } else {
            this.resend.results = results;
          }
          this.clearSelection();
          this.refreshAll(true);
        })
        .catch((err) => {
          this.$toasted.show((err && err.message) || "Resend failed");
        })
        .finally(() => {
          this.resend.submitting = false;
        });
    },
    onResendHidden() {
      this.resend.rows = [];
      this.resend.results = null;
    },
  },
};
</script>

<style>
/* ===================================================================
   Odoo Sync Logs: everything is prefixed with .osl- (unscoped so the
   drawer, modal and vue-multiselect overrides apply)
   =================================================================== */
.osl-page {
  --osl-bg: #f6f7f9;
  --osl-card: #ffffff;
  --osl-border: #e4e7ec;
  --osl-border-strong: #d0d5dd;
  --osl-text: #101828;
  --osl-text-2: #475467;
  --osl-text-3: #98a2b3;
  --osl-primary: #2563eb;
  --osl-primary-hover: #1d4ed8;
  --osl-primary-soft: #eff4ff;
  --osl-success: #067647;
  --osl-success-bg: #ecfdf3;
  --osl-danger: #b42318;
  --osl-danger-bg: #fef3f2;
  --osl-warning: #b54708;
  --osl-warning-bg: #fffaeb;
  --osl-info: #175cd3;
  --osl-info-bg: #eff8ff;
  --osl-violet: #5925dc;
  --osl-violet-bg: #f4f3ff;
  --osl-radius: 8px;
  color: var(--osl-text);
  font-size: 13px;
  padding: 4px 2px 24px;
}
.osl-page,
.osl-drawer,
.osl-modal {
  --osl-mono: "SFMono-Regular", Menlo, Consolas, "Liberation Mono", monospace;
}
.osl-drawer,
.osl-modal {
  --osl-border: #e4e7ec;
  --osl-text: #101828;
  --osl-text-2: #475467;
  --osl-text-3: #98a2b3;
  --osl-primary: #2563eb;
  --osl-primary-hover: #1d4ed8;
  --osl-primary-soft: #eff4ff;
  --osl-success: #067647;
  --osl-success-bg: #ecfdf3;
  --osl-danger: #b42318;
  --osl-danger-bg: #fef3f2;
  --osl-warning: #b54708;
  --osl-warning-bg: #fffaeb;
  --osl-info: #175cd3;
  --osl-info-bg: #eff8ff;
  --osl-violet: #5925dc;
  --osl-violet-bg: #f4f3ff;
  --osl-radius: 8px;
}

/* ---------- utilities ---------- */
.osl-muted { color: var(--osl-text-3); }
.osl-small { font-size: 11.5px; }
.osl-mono { font-family: var(--osl-mono); font-size: 11.5px; }
.osl-nowrap { white-space: nowrap; }

/* ---------- header ---------- */
.osl-header {
  display: flex;
  justify-content: space-between;
  align-items: flex-end;
  gap: 16px;
  margin-bottom: 12px;
}
.osl-header__title {
  font-size: 20px;
  font-weight: 600;
  letter-spacing: -0.01em;
  display: flex;
  align-items: center;
}
.osl-header__sub { color: var(--osl-text-2); font-size: 12.5px; margin-top: 2px; }
.osl-header__right { display: flex; align-items: center; gap: 12px; flex-shrink: 0; }
.osl-live-dot {
  display: inline-block;
  width: 7px;
  height: 7px;
  border-radius: 50%;
  background: #12b76a;
  margin-right: 4px;
  box-shadow: 0 0 0 0 rgba(18, 183, 106, 0.6);
  animation: osl-pulse 1.6s infinite;
}
@keyframes osl-pulse {
  0% { box-shadow: 0 0 0 0 rgba(18, 183, 106, 0.55); }
  70% { box-shadow: 0 0 0 6px rgba(18, 183, 106, 0); }
  100% { box-shadow: 0 0 0 0 rgba(18, 183, 106, 0); }
}

/* ---------- switch / check ---------- */
.osl-switch { display: inline-flex; align-items: center; gap: 6px; cursor: pointer; margin: 0; user-select: none; }
.osl-switch input { display: none; }
.osl-switch__track { width: 28px; height: 16px; border-radius: 10px; background: var(--osl-border-strong); position: relative; transition: background 0.15s; }
.osl-switch__thumb { position: absolute; top: 2px; left: 2px; width: 12px; height: 12px; border-radius: 50%; background: #fff; transition: transform 0.15s; box-shadow: 0 1px 2px rgba(16, 24, 40, 0.2); }
.osl-switch input:checked + .osl-switch__track { background: var(--osl-primary); }
.osl-switch input:checked + .osl-switch__track .osl-switch__thumb { transform: translateX(12px); }
.osl-check { display: inline-flex; align-items: center; gap: 6px; margin: 0; cursor: pointer; color: var(--osl-text-2); }
.osl-check input { margin: 0; }

/* ---------- buttons ---------- */
.osl-btn {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  height: 32px;
  padding: 0 12px;
  border-radius: 6px;
  border: 1px solid transparent;
  font-size: 12.5px;
  font-weight: 500;
  cursor: pointer;
  white-space: nowrap;
  transition: background 0.12s, border-color 0.12s, color 0.12s;
  line-height: 1;
}
.osl-btn:disabled { opacity: 0.5; cursor: not-allowed; }
.osl-btn--sm { height: 28px; padding: 0 10px; font-size: 12px; }
.osl-btn--xs { height: 24px; padding: 0 8px; font-size: 11.5px; }
.osl-btn--primary { background: var(--osl-primary); color: #fff; }
.osl-btn--primary:not(:disabled):hover { background: var(--osl-primary-hover); }
.osl-btn--secondary { background: #fff; color: var(--osl-text); border-color: var(--osl-border-strong); }
.osl-btn--secondary:not(:disabled):hover { background: #f9fafb; }
.osl-btn--ghost { background: transparent; color: var(--osl-text-2); border-color: var(--osl-border); }
.osl-btn--ghost:not(:disabled):hover { background: #f2f4f7; color: var(--osl-text); }
.osl-btn--link { background: transparent; color: var(--osl-primary); padding: 0 4px; }
.osl-btn--link:hover { text-decoration: underline; }

.osl-icon-btn {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 26px;
  height: 26px;
  border-radius: 6px;
  border: 1px solid transparent;
  background: transparent;
  color: var(--osl-text-2);
  cursor: pointer;
  font-size: 12.5px;
  padding: 0;
}
.osl-icon-btn:not(:disabled):hover { background: #f2f4f7; color: var(--osl-text); border-color: var(--osl-border); }
.osl-icon-btn:disabled { opacity: 0.35; cursor: not-allowed; }
.osl-icon-btn--primary { color: var(--osl-primary); }
.osl-icon-btn--lg { width: 32px; height: 32px; font-size: 15px; }
.osl-icon-btn--xs { width: 20px; height: 20px; font-size: 10.5px; }

/* ---------- KPI strip ---------- */
.osl-kpis { display: grid; grid-template-columns: repeat(6, minmax(0, 1fr)); gap: 8px; margin-bottom: 12px; }
.osl-kpi {
  text-align: left;
  background: var(--osl-card);
  border: 1px solid var(--osl-border);
  border-radius: var(--osl-radius);
  padding: 9px 12px 10px;
  cursor: pointer;
  position: relative;
  transition: border-color 0.12s, box-shadow 0.12s;
}
.osl-kpi:hover { border-color: var(--osl-border-strong); }
.osl-kpi.is-active { border-color: var(--osl-primary); box-shadow: 0 0 0 3px var(--osl-primary-soft); }
.osl-kpi::before { content: ""; position: absolute; left: 0; top: 10px; bottom: 10px; width: 3px; border-radius: 0 3px 3px 0; background: var(--osl-border); }
.osl-kpi--danger::before { background: #f04438; }
.osl-kpi--danger-soft::before { background: #fda29b; }
.osl-kpi--warning::before { background: #f79009; }
.osl-kpi--success::before { background: #17b26a; }
.osl-kpi--info::before { background: #2e90fa; }
.osl-kpi__label { font-size: 11.5px; color: var(--osl-text-2); font-weight: 500; white-space: nowrap; overflow: hidden; text-overflow: ellipsis; }
.osl-kpi__label .fa { width: 13px; color: var(--osl-text-3); }
.osl-kpi__value { font-size: 20px; font-weight: 600; margin-top: 2px; font-variant-numeric: tabular-nums; letter-spacing: -0.01em; }
.osl-kpi--danger .osl-kpi__value { color: var(--osl-danger); }

/* ---------- cards ---------- */
.osl-card { background: var(--osl-card); border: 1px solid var(--osl-border); border-radius: var(--osl-radius); }

/* ---------- filters ---------- */
.osl-filters { padding: 10px 12px; margin-bottom: 10px; }
.osl-filters__grid { display: grid; grid-template-columns: 2.2fr 1fr 1.6fr 1.2fr 1.2fr 1.6fr 1fr 1fr; gap: 8px; align-items: end; }
.osl-field { min-width: 0; }
.osl-field__label { display: block; font-size: 11px; font-weight: 600; color: var(--osl-text-2); margin: 0 0 3px; text-transform: uppercase; letter-spacing: 0.03em; }
.osl-input {
  width: 100%;
  height: 32px;
  border: 1px solid var(--osl-border-strong);
  border-radius: 6px;
  padding: 0 9px;
  font-size: 12.5px;
  color: var(--osl-text);
  background: #fff;
  outline: none;
  transition: border-color 0.12s, box-shadow 0.12s;
}
.osl-input:focus { border-color: var(--osl-primary); box-shadow: 0 0 0 3px var(--osl-primary-soft); }
.osl-select { padding-right: 4px; cursor: pointer; }
.osl-select--xs { height: 26px; width: auto; font-size: 12px; }
.osl-input-icon { position: relative; }
.osl-input-icon > .fa { position: absolute; left: 10px; top: 50%; transform: translateY(-50%); color: var(--osl-text-3); font-size: 12px; }
.osl-input-icon .osl-input { padding-left: 30px; padding-right: 28px; }
.osl-input-clear { position: absolute; right: 4px; top: 50%; transform: translateY(-50%); border: 0; background: transparent; color: var(--osl-text-3); width: 22px; height: 22px; cursor: pointer; }
.osl-daterange { display: flex; align-items: center; gap: 4px; }
.osl-daterange .osl-input { padding: 0 6px; min-width: 0; }
.osl-filters__footer { display: flex; justify-content: space-between; align-items: center; margin-top: 8px; padding-top: 8px; border-top: 1px dashed var(--osl-border); gap: 12px; flex-wrap: wrap; }
.osl-filters__right { display: flex; align-items: center; gap: 12px; }
.osl-quick { display: flex; align-items: center; gap: 4px; flex-wrap: wrap; }
.osl-chip { height: 24px; padding: 0 10px; border-radius: 12px; border: 1px solid var(--osl-border); background: #fff; font-size: 11.5px; color: var(--osl-text-2); cursor: pointer; }
.osl-chip:hover { border-color: var(--osl-border-strong); color: var(--osl-text); }
.osl-chip.is-active { background: var(--osl-primary-soft); border-color: #b2ccff; color: var(--osl-primary); font-weight: 600; }

/* vue-multiselect, compact */
.osl-ms.multiselect { min-height: 32px; font-size: 12.5px; }
.osl-ms .multiselect__tags { min-height: 32px; padding: 5px 30px 0 9px; border: 1px solid var(--osl-border-strong); border-radius: 6px; font-size: 12.5px; }
.osl-ms.multiselect--active .multiselect__tags { border-color: var(--osl-primary); }
.osl-ms .multiselect__select { height: 30px; width: 28px; }
.osl-ms .multiselect__placeholder { color: var(--osl-text-3); margin-bottom: 5px; padding-top: 0; font-size: 12.5px; }
.osl-ms .multiselect__input,
.osl-ms .multiselect__single { font-size: 12.5px; margin-bottom: 5px; padding-left: 0; min-height: 18px; line-height: 18px; }
.osl-ms .multiselect__content-wrapper { border-color: var(--osl-border-strong); box-shadow: 0 8px 24px rgba(16, 24, 40, 0.12); border-radius: 0 0 6px 6px; min-width: 240px; z-index: 60; }
.osl-ms .multiselect__option { min-height: 30px; padding: 7px 10px; font-size: 12.5px; line-height: 16px; }
.osl-ms .multiselect__option--highlight { background: var(--osl-primary-soft); color: var(--osl-text); }
.osl-ms .multiselect__option--selected { background: #f2f4f7; font-weight: 600; }
.osl-ms .multiselect__option--selected::after,
.osl-ms .multiselect__option--highlight::after { display: none; }
.osl-ms .multiselect__option--group { background: #f9fafb !important; color: var(--osl-text-3) !important; font-size: 10.5px; font-weight: 700; text-transform: uppercase; letter-spacing: 0.05em; min-height: 26px; padding: 6px 10px; }
.osl-ms__summary { display: inline-block; font-size: 12.5px; color: var(--osl-text); margin-bottom: 5px; line-height: 18px; }
.osl-ms__opt { display: flex; justify-content: space-between; gap: 8px; }
.osl-ms__code { font-family: var(--osl-mono); font-size: 10.5px; color: var(--osl-text-3); }

/* ---------- bulk bar ---------- */
.osl-bulkbar {
  position: sticky;
  top: 8px;
  z-index: 20;
  display: flex;
  align-items: center;
  justify-content: space-between;
  background: #101828;
  color: #fff;
  border-radius: var(--osl-radius);
  padding: 7px 8px 7px 14px;
  margin-bottom: 8px;
  box-shadow: 0 8px 20px rgba(16, 24, 40, 0.18);
}
.osl-bulkbar .osl-muted { color: #98a2b3; }
.osl-bulkbar__actions { display: flex; gap: 6px; }
.osl-bulkbar .osl-btn--ghost { color: #d0d5dd; border-color: #344054; }
.osl-bulkbar .osl-btn--ghost:hover { background: #1d2939; color: #fff; }

/* ---------- table ---------- */
.osl-table-card { overflow: hidden; }
.osl-table-wrap { overflow: auto; max-height: calc(100vh - 360px); min-height: 280px; }
.osl-table { width: 100%; border-collapse: separate; border-spacing: 0; font-size: 12.5px; }
.osl-table thead th {
  position: sticky;
  top: 0;
  z-index: 2;
  background: #f9fafb;
  color: var(--osl-text-2);
  font-size: 11px;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.03em;
  text-align: left;
  padding: 8px 8px;
  border-bottom: 1px solid var(--osl-border);
  white-space: nowrap;
}
.osl-table td { padding: 6px 8px; border-bottom: 1px solid #f2f4f7; vertical-align: middle; color: var(--osl-text); }
.osl-table tbody.is-refreshing { opacity: 0.6; transition: opacity 0.15s; }
.osl-row { cursor: pointer; }
.osl-row td:first-child { box-shadow: inset 3px 0 0 transparent; }
.osl-row--failed td:first-child { box-shadow: inset 3px 0 0 #f04438; }
.osl-row--pending td:first-child { box-shadow: inset 3px 0 0 #f79009; }
.osl-row:hover td { background: #f9fafb; }
.osl-row.is-selected td { background: var(--osl-primary-soft); }
.osl-row.is-open td { background: #eef4ff; }
.osl-row.is-superseded td { color: var(--osl-text-3); }
.osl-row.is-superseded .osl-badge { opacity: 0.6; }
.osl-col-check { width: 32px; text-align: center; padding-left: 10px !important; }
.osl-col-check input { cursor: pointer; vertical-align: middle; }
.osl-col-actions { width: 92px; text-align: right; }
.osl-col-error { min-width: 180px; max-width: 320px; }
.osl-row-actions { display: inline-flex; gap: 2px; opacity: 0.55; transition: opacity 0.12s; }
.osl-row:hover .osl-row-actions,
.osl-row.is-open .osl-row-actions { opacity: 1; }
.osl-cell-main { font-weight: 500; line-height: 1.3; }
.osl-cell-sub { font-size: 11px; color: var(--osl-text-3); line-height: 1.3; display: flex; gap: 5px; align-items: center; max-width: 220px; overflow: hidden; text-overflow: ellipsis; white-space: nowrap; }
.osl-cell-ref { max-width: 170px; overflow: hidden; text-overflow: ellipsis; white-space: nowrap; }
.osl-cell-endpoint { max-width: 220px; overflow: hidden; text-overflow: ellipsis; white-space: nowrap; color: var(--osl-text-2); }
.osl-error-text {
  color: var(--osl-danger);
  font-size: 12px;
  display: -webkit-box;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
  overflow: hidden;
  line-height: 1.35;
  word-break: break-word;
}
.osl-method { display: inline-block; font-family: var(--osl-mono); font-size: 10px; font-weight: 700; color: var(--osl-primary); background: var(--osl-primary-soft); border-radius: 3px; padding: 1px 4px; margin-right: 5px; }
.osl-action { font-size: 10px; font-weight: 700; text-transform: uppercase; letter-spacing: 0.04em; border-radius: 3px; padding: 0 4px; background: #f2f4f7; color: var(--osl-text-2); }
.osl-action--add,
.osl-action--create { background: var(--osl-success-bg); color: var(--osl-success); }
.osl-action--update,
.osl-action--re-sync { background: var(--osl-info-bg); color: var(--osl-info); }
.osl-http { font-family: var(--osl-mono); font-size: 11.5px; font-weight: 600; }
.osl-http.is-ok { color: var(--osl-success); }
.osl-http.is-warn { color: var(--osl-warning); }
.osl-http.is-err { color: var(--osl-danger); }
.osl-http.is-none { color: var(--osl-text-3); font-weight: 400; }

/* ---------- badges & tags ---------- */
.osl-badge { display: inline-flex; align-items: center; height: 20px; padding: 0 7px; border-radius: 10px; font-size: 11px; font-weight: 600; white-space: nowrap; border: 1px solid transparent; }
.osl-badge--success { background: var(--osl-success-bg); color: var(--osl-success); border-color: #abefc6; }
.osl-badge--danger { background: var(--osl-danger-bg); color: var(--osl-danger); border-color: #fecdca; }
.osl-badge--warning { background: var(--osl-warning-bg); color: var(--osl-warning); border-color: #fedf89; }
.osl-badge--info { background: var(--osl-info-bg); color: var(--osl-info); border-color: #b2ddff; }
.osl-badge--neutral { background: #f2f4f7; color: var(--osl-text-2); border-color: var(--osl-border); }
.osl-badge--success-soft { background: transparent; color: var(--osl-success); border-color: #abefc6; }
.osl-badge--danger-soft { background: transparent; color: var(--osl-danger); border-color: #fecdca; }
.osl-badge--pulse { animation: osl-badge-pulse 1.2s ease-in-out infinite; }
@keyframes osl-badge-pulse { 50% { opacity: 0.55; } }
.osl-tag { display: inline-flex; align-items: center; height: 18px; padding: 0 6px; border-radius: 4px; font-size: 10.5px; font-weight: 600; background: #f2f4f7; color: var(--osl-text-2); white-space: nowrap; }
.osl-tag--blue { background: var(--osl-info-bg); color: var(--osl-info); }
.osl-tag--violet { background: var(--osl-violet-bg); color: var(--osl-violet); }

/* ---------- skeleton / empty ---------- */
.osl-skeleton { display: inline-block; height: 10px; width: 80%; border-radius: 4px; background: linear-gradient(90deg, #f2f4f7 25%, #e4e7ec 50%, #f2f4f7 75%); background-size: 200% 100%; animation: osl-shimmer 1.2s infinite; }
.osl-skeleton--num { height: 20px; width: 50px; }
@keyframes osl-shimmer { 0% { background-position: 200% 0; } 100% { background-position: -200% 0; } }
.osl-row--skeleton td { padding: 12px 8px; }
.osl-empty { text-align: center; padding: 48px 12px; }
.osl-empty > .fa { font-size: 28px; color: var(--osl-text-3); }
.osl-empty__title { font-weight: 600; margin-top: 8px; }

/* ---------- pager ---------- */
.osl-pager { display: flex; justify-content: space-between; align-items: center; padding: 8px 12px; border-top: 1px solid var(--osl-border); background: #fcfcfd; }
.osl-pager__right { display: flex; align-items: center; gap: 3px; }
.osl-pager__btn { min-width: 26px; height: 26px; padding: 0 6px; border: 1px solid var(--osl-border); border-radius: 5px; background: #fff; color: var(--osl-text-2); font-size: 12px; cursor: pointer; }
.osl-pager__btn:not(:disabled):hover { border-color: var(--osl-border-strong); color: var(--osl-text); }
.osl-pager__btn.is-active { background: var(--osl-primary); border-color: var(--osl-primary); color: #fff; font-weight: 600; }
.osl-pager__btn:disabled { opacity: 0.45; cursor: default; }

/* ---------- callouts ---------- */
.osl-callout { display: flex; gap: 10px; align-items: flex-start; border-radius: 6px; padding: 9px 11px; margin-bottom: 10px; font-size: 12.5px; border: 1px solid transparent; }
.osl-callout > .fa { margin-top: 2px; }
.osl-callout__title { font-weight: 600; margin-bottom: 1px; }
.osl-callout__text { word-break: break-word; white-space: pre-wrap; }
.osl-callout--danger { background: var(--osl-danger-bg); color: #7a271a; border-color: #fecdca; }
.osl-callout--warning { background: var(--osl-warning-bg); color: #7a2e0e; border-color: #fedf89; }
.osl-callout--info { background: var(--osl-info-bg); color: #194185; border-color: #b2ddff; }
.osl-callout--success { background: var(--osl-success-bg); color: #054f31; border-color: #abefc6; }
.osl-callout--neutral { background: #f9fafb; color: var(--osl-text-2); border-color: var(--osl-border); }

/* ---------- drawer ---------- */
.osl-drawer-backdrop { position: fixed; inset: 0; top: 0; left: 0; right: 0; bottom: 0; background: rgba(16, 24, 40, 0.35); z-index: 1040; }
.osl-drawer {
  position: fixed;
  top: 0;
  right: 0;
  bottom: 0;
  width: 760px;
  max-width: 96vw;
  background: #fff;
  z-index: 1041;
  display: flex;
  flex-direction: column;
  box-shadow: -12px 0 32px rgba(16, 24, 40, 0.18);
  color: var(--osl-text);
  font-size: 13px;
}
.osl-drawer__header { display: flex; justify-content: space-between; align-items: flex-start; gap: 12px; padding: 14px 16px 12px; border-bottom: 1px solid var(--osl-border); }
.osl-drawer__title-wrap { min-width: 0; }
.osl-drawer__eyebrow { font-size: 11px; color: var(--osl-text-3); text-transform: uppercase; letter-spacing: 0.04em; font-weight: 600; }
.osl-drawer__title { font-size: 16px; font-weight: 600; display: flex; align-items: center; flex-wrap: wrap; margin-top: 2px; }
.osl-drawer__uid { display: flex; align-items: center; gap: 4px; color: var(--osl-text-2); margin-top: 4px; }
.osl-drawer__body { flex: 1; overflow: auto; padding: 12px 16px 24px; }
.osl-drawer__loading { flex: 1; display: flex; flex-direction: column; align-items: center; justify-content: center; gap: 10px; color: var(--osl-text-3); }
.osl-drawer__actions { display: flex; align-items: center; gap: 6px; margin-bottom: 12px; flex-wrap: wrap; }
.osl-spinner { width: 22px; height: 22px; border: 2px solid var(--osl-border); border-top-color: var(--osl-primary); border-radius: 50%; animation: osl-spin 0.7s linear infinite; }
@keyframes osl-spin { to { transform: rotate(360deg); } }

.osl-meta { display: grid; grid-template-columns: repeat(4, minmax(0, 1fr)); border: 1px solid var(--osl-border); border-radius: 6px; overflow: hidden; margin-bottom: 12px; }
.osl-meta__item { padding: 7px 10px; border-right: 1px solid #f2f4f7; border-bottom: 1px solid #f2f4f7; min-width: 0; }
.osl-meta__item--wide { grid-column: span 2; }
.osl-meta__k { font-size: 10.5px; color: var(--osl-text-3); text-transform: uppercase; letter-spacing: 0.03em; font-weight: 600; }
.osl-meta__v { margin-top: 2px; overflow: hidden; text-overflow: ellipsis; white-space: nowrap; display: flex; align-items: center; gap: 4px; }

.osl-tabs { display: flex; gap: 2px; border-bottom: 1px solid var(--osl-border); margin-bottom: 0; }
.osl-tabs__tab { border: 0; background: transparent; padding: 8px 12px; font-size: 12.5px; font-weight: 500; color: var(--osl-text-2); border-bottom: 2px solid transparent; margin-bottom: -1px; cursor: pointer; display: inline-flex; align-items: center; gap: 6px; }
.osl-tabs__tab:hover { color: var(--osl-text); }
.osl-tabs__tab.is-active { color: var(--osl-primary); border-bottom-color: var(--osl-primary); }
.osl-tabs__count { background: #f2f4f7; color: var(--osl-text-2); border-radius: 8px; font-size: 10.5px; padding: 0 6px; font-weight: 600; }

.osl-code { border: 1px solid var(--osl-border); border-top: 0; border-radius: 0 0 6px 6px; overflow: hidden; }
.osl-code__bar { display: flex; justify-content: space-between; align-items: center; padding: 5px 8px; background: #f9fafb; border-bottom: 1px solid var(--osl-border); }
.osl-code__pre { margin: 0; padding: 10px 12px; background: #0c111d; color: #e4e7ec; font-family: var(--osl-mono); font-size: 11.5px; line-height: 1.55; max-height: calc(100vh - 430px); min-height: 160px; overflow: auto; white-space: pre; }
.osl-code__pre.is-wrap { white-space: pre-wrap; word-break: break-all; }

.osl-timeline { padding: 10px 2px; }
.osl-timeline__item { display: flex; gap: 10px; padding: 8px 8px; border-radius: 6px; cursor: pointer; position: relative; }
.osl-timeline__item:not(:last-child)::after { content: ""; position: absolute; left: 13px; top: 24px; bottom: -8px; width: 1px; background: var(--osl-border); }
.osl-timeline__item:hover { background: #f9fafb; }
.osl-timeline__item.is-current { background: var(--osl-primary-soft); cursor: default; }
.osl-timeline__dot { width: 11px; height: 11px; border-radius: 50%; margin-top: 3px; flex-shrink: 0; border: 2px solid #fff; box-shadow: 0 0 0 1px var(--osl-border-strong, #d0d5dd); background: #d0d5dd; z-index: 1; }
.osl-timeline__dot.is-completed { background: #17b26a; box-shadow: 0 0 0 1px #17b26a; }
.osl-timeline__dot.is-failed { background: #f04438; box-shadow: 0 0 0 1px #f04438; }
.osl-timeline__dot.is-pending { background: #f79009; box-shadow: 0 0 0 1px #f79009; }
.osl-timeline__content { flex: 1; min-width: 0; }
.osl-timeline__row { display: flex; align-items: center; gap: 6px; flex-wrap: wrap; }
.osl-timeline__err { color: var(--osl-danger); font-size: 12px; margin: 3px 0; word-break: break-word; }

/* ---------- resend modal ---------- */
.osl-modal .modal-content { border-radius: 10px; border: 0; font-size: 13px; color: var(--osl-text); }
.osl-modal .modal-header { padding: 12px 16px; border-bottom: 1px solid var(--osl-border); }
.osl-modal .modal-title { font-size: 15px; font-weight: 600; }
.osl-modal .modal-body { padding: 14px 16px; }
.osl-modal .modal-footer { padding: 10px 16px; border-top: 1px solid var(--osl-border); gap: 6px; }
.osl-mode-cards { display: grid; grid-template-columns: 1fr 1fr; gap: 8px; margin-bottom: 12px; }
.osl-mode-card { display: flex; gap: 10px; align-items: flex-start; border: 1px solid var(--osl-border); border-radius: 8px; padding: 10px 12px; cursor: pointer; margin: 0; transition: border-color 0.12s, box-shadow 0.12s; }
.osl-mode-card input { margin-top: 3px; }
.osl-mode-card.is-active { border-color: var(--osl-primary); box-shadow: 0 0 0 3px var(--osl-primary-soft); }
.osl-mode-card__title { font-weight: 600; margin-bottom: 3px; display: flex; align-items: center; gap: 4px; }
.osl-mode-card__text { color: var(--osl-text-2); font-size: 12px; line-height: 1.45; }
.osl-resend-summary { display: grid; grid-template-columns: repeat(4, 1fr); border: 1px solid var(--osl-border); border-radius: 8px; margin-bottom: 12px; }
.osl-resend-summary__item { padding: 8px 12px; border-right: 1px solid var(--osl-border); }
.osl-resend-summary__item:last-child { border-right: 0; }
.osl-resend-summary__num { font-size: 18px; font-weight: 600; font-variant-numeric: tabular-nums; }
.osl-force { align-items: flex-start; background: #f9fafb; border: 1px solid var(--osl-border); border-radius: 6px; padding: 8px 10px; margin-bottom: 10px; width: 100%; color: var(--osl-text); }
.osl-force input { margin-top: 3px; }
.osl-result-list { border: 1px solid var(--osl-border); border-radius: 6px; padding: 8px 10px; max-height: 220px; overflow: auto; }
.osl-result-list__title { font-size: 11px; font-weight: 700; color: var(--osl-text-2); text-transform: uppercase; letter-spacing: 0.03em; margin-bottom: 4px; }
.osl-result-list__row { display: flex; justify-content: space-between; gap: 12px; padding: 4px 0; border-top: 1px solid #f2f4f7; }
.osl-result-list__row:first-of-type { border-top: 0; }

/* ---------- transitions ---------- */
.osl-fade-enter-active,
.osl-fade-leave-active { transition: opacity 0.18s; }
.osl-fade-enter,
.osl-fade-leave-to { opacity: 0; }
.osl-slide-enter-active,
.osl-slide-leave-active { transition: transform 0.22s cubic-bezier(0.2, 0.8, 0.2, 1); }
.osl-slide-enter,
.osl-slide-leave-to { transform: translateX(100%); }

/* ---------- responsive ---------- */
@media (max-width: 1500px) {
  .osl-filters__grid { grid-template-columns: repeat(4, minmax(0, 1fr)); }
  .osl-field--search { grid-column: span 2; }
}
@media (max-width: 1100px) {
  .osl-kpis { grid-template-columns: repeat(3, minmax(0, 1fr)); }
  .osl-filters__grid { grid-template-columns: repeat(2, minmax(0, 1fr)); }
  .osl-meta { grid-template-columns: repeat(2, minmax(0, 1fr)); }
  .osl-header { flex-direction: column; align-items: flex-start; }
}
</style>



// WEBPACK FOOTER //
// src/components/Pages/MasterContainer/OdooSyncLogs/odooSyncLogs.vue